import type { CmlContent } from '@coursera-web/cml';

import type { ContentSummary } from 'bundles/learner-progress/types/ContentSummary';

export enum CourseType {
  STANDARD_COURSE = 'standardCourse',
  RHYME_PROJECT = 'rhymeProject',
  PROJECT = 'project',
  VERIFIED_SKILLS_COURSE = 'verifiedSkillsCourse',
}

export type StandardCourseMetadata = {
  typeNameIndex: typeof CourseType.STANDARD_COURSE;
};

export type ProjectMetadata = {
  typeNameIndex: typeof CourseType.PROJECT;
};

export type RhymeProjectMetadata = {
  typeNameIndex: typeof CourseType.RHYME_PROJECT;
  tasks?: Array<CmlContent>;
  screenshotUrls?: Array<string>;
};

export type VerifiedSkillsCourseMetadata = {
  typeNameIndex: typeof CourseType.VERIFIED_SKILLS_COURSE;
};

export type CourseTypeMetadata = {
  typeName: CourseType;
  definition: StandardCourseMetadata | RhymeProjectMetadata | ProjectMetadata | VerifiedSkillsCourseMetadata | {};
};

/**
 * Manually created `RawCourseMaterials` describes the `courseMaterialsData`
 * passed as payload to `LOAD_COURSE_MATERIALS` action. The data
 * is sourced from the `onDemandCourseMaterials.v2` endpoint, and is linked to
 * `onDemandCourseMaterialItems.v2`, `onDemandCourseMaterialLessons.v1`,
 * `onDemandCourseMaterialModules.v1` and other related endpoints. The current
 * method of fetching the data utilizes URI and Q without using generated types.
 *
 * @see static/bundles/course-v2/utils/loadCourseMaterials.ts
 */
export type RawCourseMaterials = {
  courseData?: unknown;
  courseMaterialsData?: CourseMaterialsData;
};

/**
 * Describes data structure passed to `CourseMaterials` Backbone constructor.
 *
 * @see static/pages/open-course/common/models/courseMaterials.ts
 */
export type CourseMaterialsData = {
  elements: CourseMaterialsDataModule[];
  gradePolicy: Array<Record<string, unknown>> | null;
  gradingParameters: GradingParameters;
  totalGradingWeight: number;
  tracks: {
    core: {
      id: string;
      passablesCount: number;
    };
  };
};

type GradingParameters = {
  gradedAssignmentGroups: Record<
    string,
    {
      assignmentIds: string[];
      weight: number;
    }
  >;
};

/**
 * Describes data structure passed to `Module` Backbone constructor.
 *
 * @see static/pages/open-course/common/models/module.ts
 */
export type CourseMaterialsDataModule = {
  description: string;
  elements: CourseMaterialsDataModuleLesson[];
  id: string;
  learningObjectives: string[];
  name: string;
  slug: string;
  timeCommitment: number;
};

/**
 * Describes data structure passed to `Lesson` Backbone constructor.
 *
 * @see static/pages/open-course/common/models/lesson.ts
 */
export type CourseMaterialsDataModuleLesson = {
  elements: CourseMaterialsDataModuleLessonItem[];
  id: string;
  itemGroups: unknown;
  name: string;
  slug: string;
  timeCommitment: number;
  trackId: string;
};

/**
 * Describes data structure passed to `ItemMetadata` Backbone constructor.
 * There is no explicit typing for this raw object.
 *
 * From `ItemMetadata` Backbone constructor:
 * > ItemMetadata contains data about a course item and its contents.
 * > ItemMetadata's attributes are exactly the attributes returned by the course
 * > data API, plus an attribute called `lesson` that refers to the parent
 * > lesson for this item.
 *
 * Instance of ItemMetadata is later used to create `computedItem` object, typed
 * as `static/bundles/learner-progress/types/Item.ts`.
 * The `computedItem` uses `ContentSummary` type described in
 * `static/bundles/learner-progress/types/ContentSummary.ts`. Hence we're
 * relying on this one.
 *
 * Note, there is one more version of `ContentSummary` type in
 * `static/bundles/course-v2/types/ContentSummary.ts`!
 *
 * @see static/pages/open-course/common/models/itemMetadata.ts
 */
export type CourseMaterialsDataModuleLessonItem = {
  contentSummary: ContentSummary;
  gradingWeight?: number;
  id: string;
  isLocked?: boolean;
  isRequiredForPassing?: boolean;
  itemLockedReasonCode?: string;
  lockedStatus?: string;
  name: string;
  slug: string;
  timeCommitment: number;
  trackId: string;
};
